import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec } from '../helpers/stringFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import firstCharToUpper from '../helpers/stringFormat';
import { tooltip, generalTooltipText } from '../helpers/tooltip';

const revenueModels = [
    'subscription',
    'transactional'
]

const RevenueValueDriverCLVForm = forwardRef(({ currency, outcome, outcomeList, editCLVDriverId, editCLVDriver, formActions, onSubmit }, ref) => {
    const [open, setOpen] = useState(false);
    const [openOutcome, setOpenOutcome] = useState(false);
    const [positiveDriver, setPositiveDriver] = useState(true);

    const tooltipText = {
        acvChange: `${positiveDriver ? "Increase" : "Decrease"} in ACV when using your product instead of the competitive alternative.`,
        contractLength: "Average Contract length for your customer's customers.",
        contractLengthChange: `${positiveDriver ? "Increase" : "Decrease"} in Average Contract length when using your product instead of the competitive alternative.`,
        churn: "Churn rate at end of contract represents the percentage of customers who do not renew their contracts once they expire. It reflects customer retention and the effectiveness of your value proposition at the contract's conclusion.",
        churnChange: `${positiveDriver ? "Decrease" : "Increase"} in Churn rate when using your product instead of the competitive alternative.`,
        transactionValueChange: `${positiveDriver ? "Increase" : "Decrease"} in Transaction value when using your product instead of the competitive alternative.`,
        transactionFrequencyChange: `${positiveDriver ? "Increase" : "Decrease"} in Annual transaction frequency when using your product instead of the competitive alternative.`,
    }

    const handleRevenueModelChange = (event) => {
        formik.setFieldValue('revenue_model', event.target.value)
    };

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    useEffect(() => {
        formik.setFieldValue("outcome", outcomeList[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcomeList]);

    useEffect(() => {
        if (editCLVDriverId) {
            setPositiveDriver(editCLVDriver["positive_driver"])

            formik.setFieldValue("conversion", editCLVDriver["conversion"])
            formik.setFieldValue("conversion_increase", editCLVDriver["conversion_increase"])
            formik.setFieldValue("revenue_model", editCLVDriver["revenue_model"])
            formik.setFieldValue("annual_contract_value", editCLVDriver["annual_contract_value"])
            formik.setFieldValue("contract_length", editCLVDriver["contract_length"])
            formik.setFieldValue("churn_rate", editCLVDriver["churn_rate"])
            formik.setFieldValue("transaction_value", editCLVDriver["transaction_value"])
            formik.setFieldValue("transaction_frequency", editCLVDriver["transaction_frequency"])
            formik.setFieldValue("contribution_margin", editCLVDriver["contribution_margin"])
            formik.setFieldValue("clv_change", editCLVDriver["clv_change"])
            formik.setFieldValue("positive_driver", editCLVDriver["positive_driver"])
            formik.setFieldValue("outcome", editCLVDriver["outcome"])
            formik.setFieldValue("driver_name", editCLVDriver["driver_name"])
            formik.setFieldValue("comment", editCLVDriver["comment"])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editCLVDriverId])

    useEffect(() => {
        formik.setFieldValue("driver_name", `Lifetime value ${positiveDriver ? 'Increase' : 'Decrease'}`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver])

    const clvChangeInitialValues = {
        annual_contract_value: 0,
        transaction_value: 0,
        transaction_frequency: 0,
        contract_length: 0,
        churn_rate: 0
    }

    const initialValues = {
        id: null,
        revenue_type: 'conversion',
        outcome: '',
        driver_name: `Lifetime value ${positiveDriver ? 'Increase' : 'Decrease'}`,
        comment: '',
        positive_driver: true,
        type: '',
        currency: currency,
        annual_contract_value: 0,
        contract_length: 12,
        churn_rate: 5,
        transaction_value: 0,
        transaction_frequency: 0,
        customer_lifespan: 12,
        contribution_margin: 0,
        revenue_model: revenueModels[0],
        clv_change: clvChangeInitialValues
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {
            clv_change: {}
        };

        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }

        Object.keys(clvChangeInitialValues).forEach(type => {
            if (values.revenue_model === 'transactional' && type === 'transaction_frequency') {
                if (!isNumeric(values.clv_change[type])) {
                    errors.clv_change[type] = 'Must be numeric value';
                } else if (!isPositive(values.clv_change[type])) {
                    errors.clv_change[type] = 'Must be positive or zero';
                }
            }
            else if (values.revenue_model === 'transactional' && type === 'transaction_value') {
                if (!isNumeric(values.clv_change[type])) {
                    errors.clv_change[type] = 'Must be numeric value';
                } else if (!isPositive(values.clv_change[type])) {
                    errors.clv_change[type] = 'Must be positive or zero';
                } else if (values.clv_change[type] > 100) {
                    errors.clv_change[type] = 'Not more than 100';
                }
            }
            else if (values.revenue_model === 'subscription') {
                if (!isNumeric(values.clv_change[type])) {
                    errors.clv_change[type] = 'Must be numeric value';
                } else if (!isPositive(values.clv_change[type])) {
                    errors.clv_change[type] = 'Must be positive or zero';
                } else if (values.clv_change[type] > 100) {
                    errors.clv_change[type] = 'Not more than 100';
                }
            }
        })

        if (!isNumeric(values.annual_contract_value)) {
            errors.annual_contract_value = 'Must be numeric value';
        } else if (!isPositive(values.annual_contract_value)) {
            errors.annual_contract_value = 'Must be positive or zero';
        }

        if (!isNumeric(values.contract_length)) {
            errors.contract_length = 'Must be numeric value';
        } else if (!isPositive(values.contract_length)) {
            errors.contract_length = 'Must be positive or zero';
        }

        if (!isNumeric(values.contribution_margin)) {
            errors.contribution_margin = 'Must be numeric value';
        } else if (!isPositive(values.contribution_margin)) {
            errors.contribution_margin = 'Must be positive or zero';
        } else if (values.contribution_margin > 100) {
            errors.contribution_margin = 'Not more than 100';
        }

        if (!isNumeric(values.transaction_value)) {
            errors.transaction_value = 'Must be numeric value';
        } else if (!isPositive(values.transaction_value)) {
            errors.transaction_value = 'Must be positive or zero';
        }

        if (!isNumeric(values.transaction_frequency)) {
            errors.transaction_frequency = 'Must be numeric value';
        } else if (!isPositive(values.transaction_frequency)) {
            errors.transaction_frequency = 'Must be positive or zero';
        }

        if (!isNumeric(values.customer_lifespan)) {
            errors.customer_lifespan = 'Must be numeric value';
        } else if (!isPositive(values.customer_lifespan)) {
            errors.customer_lifespan = 'Must be positive or zero';
        }

        if (!isNumeric(values.churn_rate)) {
            errors.churn_rate = 'Must be numeric value';
        } else if (!isPositive(values.churn_rate)) {
            errors.churn_rate = 'Must be positive or zero';
        } else if (values.churn_rate > 100) {
            errors.churn_rate = 'Not more than 100';
        }

        // Filter out empty nested error objects for clv_change
        if (Object.keys(errors.clv_change).length === 0) {
            delete errors.clv_change;
        }

        // If the errors object is empty (meaning no errors), return an empty object
        if (Object.keys(errors).length === 0) {
            return {};
        }

        console.log(errors)

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            if (!outcome) {
                formik.setFieldValue("outcome", outcomeList[0] || '')
            }
            setPositiveDriver(true)
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1300 }}>
            <Grid container columnSpacing={2} alignItems="center" rowSpacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            1. Calculate based on your customers' revenue model
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption">
                            Your Customers' revenue model{tooltip(generalTooltipText.revenueModel)}
                        </Typography>
                        <Box>
                            <Select
                                open={open}
                                onClose={handleClose}
                                onOpen={handleOpen}
                                value={formik.values.revenue_model}
                                onChange={handleRevenueModelChange}
                                size='small'
                            >
                                {revenueModels.map((model) => (
                                    <MenuItem key={model} value={model}>
                                        {firstCharToUpper(model)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Stack>
                </Grid>
                <Grid item xs={10} />
                {formik.values.revenue_model === 'subscription' ?
                    <React.Fragment>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Annual Contract Value (ACV){tooltip(generalTooltipText.acv)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Contribution margin{tooltip(generalTooltipText.contributionMargin)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        {`ACV ${positiveDriver ? 'Increase' : 'Decrease'}`}{tooltip(tooltipText.acvChange)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="annual_contract_value"
                                        startAdornment={currency}
                                        value={formik.values.annual_contract_value}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("annual_contract_value", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "annual_contract_value") && Boolean(getIn(formik.errors, "annual_contract_value"))}
                                        helperText={getIn(formik.touched, "annual_contract_value") && getIn(formik.errors, "annual_contract_value")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="contribution_margin"
                                        endAdornment="%"
                                        value={formik.values.contribution_margin}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("contribution_margin", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "contribution_margin") && Boolean(getIn(formik.errors, "contribution_margin"))}
                                        helperText={getIn(formik.touched, "contribution_margin") && getIn(formik.errors, "contribution_margin")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="clv_change.annual_contract_value"
                                        endAdornment="%"
                                        value={formik.values.clv_change.annual_contract_value}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("clv_change.annual_contract_value", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "clv_change.annual_contract_value") && Boolean(getIn(formik.errors, "clv_change.annual_contract_value"))}
                                        helperText={getIn(formik.touched, "clv_change.annual_contract_value") && getIn(formik.errors, "clv_change.annual_contract_value")}
                                        greenBackground={positiveDriver}
                                        redBackground={!positiveDriver}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Contract length{tooltip(tooltipText.contractLength)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        {`Contract length ${positiveDriver ? 'Increase' : 'Decrease'}`}{tooltip(tooltipText.contractLengthChange)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="contract_length"
                                        endAdornment={
                                            <Typography variant="caption">
                                                months
                                            </Typography>
                                        }
                                        value={formik.values.contract_length}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("contract_length", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "contract_length") && Boolean(getIn(formik.errors, "contract_length"))}
                                        helperText={getIn(formik.touched, "contract_length") && getIn(formik.errors, "contract_length")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="clv_change.contract_length"
                                        endAdornment="%"
                                        value={formik.values.clv_change.contract_length}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("clv_change.contract_length", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "clv_change.contract_length") && Boolean(getIn(formik.errors, "clv_change.contract_length"))}
                                        helperText={getIn(formik.touched, "clv_change.contract_length") && getIn(formik.errors, "clv_change.contract_length")}
                                        greenBackground={positiveDriver}
                                        redBackground={!positiveDriver}
                                    />
                                    <Grid item xs={8} />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Churn rate at end of contract{tooltip(tooltipText.churn)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        {`Churn rate ${positiveDriver ? 'Decrease' : 'Increase'}`}{tooltip(tooltipText.churnChange)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="churn_rate"
                                        endAdornment="%"
                                        value={formik.values.churn_rate}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("churn_rate", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "churn_rate") && Boolean(getIn(formik.errors, "churn_rate"))}
                                        helperText={getIn(formik.touched, "churn_rate") && getIn(formik.errors, "churn_rate")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="clv_change.churn_rate"
                                        endAdornment="%"
                                        value={formik.values.clv_change.churn_rate}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("clv_change.churn_rate", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "clv_change.churn_rate") && Boolean(getIn(formik.errors, "clv_change.churn_rate"))}
                                        helperText={getIn(formik.touched, "clv_change.churn_rate") && getIn(formik.errors, "clv_change.churn_rate")}
                                        greenBackground={positiveDriver}
                                        redBackground={!positiveDriver}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                    : null}
                {formik.values.revenue_model === 'transactional' ?
                    <React.Fragment>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Transaction value{tooltip(generalTooltipText.transactionValue)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Contribution margin{tooltip(generalTooltipText.contributionMargin)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        {`Transaction value ${positiveDriver ? 'Increase' : 'Decrease'}`}{tooltip(tooltipText.transactionValueChange)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="transaction_value"
                                        startAdornment={currency}
                                        value={formik.values.transaction_value}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("transaction_value", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "transaction_value") && Boolean(getIn(formik.errors, "transaction_value"))}
                                        helperText={getIn(formik.touched, "transaction_value") && getIn(formik.errors, "transaction_value")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="contribution_margin"
                                        endAdornment="%"
                                        value={formik.values.contribution_margin}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("contribution_margin", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "contribution_margin") && Boolean(getIn(formik.errors, "contribution_margin"))}
                                        helperText={getIn(formik.touched, "contribution_margin") && getIn(formik.errors, "contribution_margin")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="clv_change.transaction_value"
                                        endAdornment="%"
                                        value={formik.values.clv_change.transaction_value}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("clv_change.transaction_value", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "clv_change.transaction_value") && Boolean(getIn(formik.errors, "clv_change.transaction_value"))}
                                        helperText={getIn(formik.touched, "clv_change.transaction_value") && getIn(formik.errors, "clv_change.transaction_value")}
                                        greenBackground={positiveDriver}
                                        redBackground={!positiveDriver}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Annual transaction frequency{tooltip(generalTooltipText.transactionFrequency)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        {`Transaction frequency ${positiveDriver ? 'Increase' : 'Decrease'}`}{tooltip(tooltipText.transactionFrequencyChange)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="transaction_frequency"
                                        endAdornment={
                                            <Typography variant="caption">
                                                times/year
                                            </Typography>
                                        }
                                        value={formik.values.transaction_frequency}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("transaction_frequency", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "transaction_frequency") && Boolean(getIn(formik.errors, "transaction_frequency"))}
                                        helperText={getIn(formik.touched, "transaction_frequency") && getIn(formik.errors, "transaction_frequency")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="clv_change.transaction_frequency"
                                        endAdornment={
                                            <Typography variant="caption">
                                                times/year
                                            </Typography>
                                        }
                                        value={formik.values.clv_change.transaction_frequency}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("clv_change.transaction_frequency", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "clv_change.transaction_frequency") && Boolean(getIn(formik.errors, "clv_change.transaction_frequency"))}
                                        helperText={getIn(formik.touched, "clv_change.transaction_frequency") && getIn(formik.errors, "clv_change.transaction_frequency")}
                                        greenBackground={positiveDriver}
                                        redBackground={!positiveDriver}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                    </React.Fragment>
                    : null}
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            2. Driver details
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption" sx={{ mr: 1 }}>
                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                        </Typography>
                        <GreenRedCompactSwitch
                            name="positive_driver"
                            leftLabel="Negative"
                            rightLabel="Positive"
                            value={formik.values.positive_driver}
                            onChange={(event) => {
                                formik.handleChange(event)
                                setPositiveDriver(!positiveDriver)
                            }}
                        />

                    </Stack>
                </Grid>
                <Grid item xs={10} />
                {!outcome &&
                    (<React.Fragment>
                        <Grid item xs={2}>
                            <Stack direction="column" gap={1}>
                                <Typography variant="caption">
                                    Outcome
                                </Typography>
                                <Box>
                                    <Select
                                        open={openOutcome}
                                        onClose={handleOutcomeClose}
                                        onOpen={handleOutcomeOpen}
                                        value={formik.values.outcome}
                                        onChange={handleOutcomeChange}
                                        size='small'
                                    >
                                        {outcomeList.map((outcome) => (
                                            <MenuItem key={outcome} value={outcome}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                    {outcome}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={10} />
                    </React.Fragment>)
                }
                <Grid item xs={12}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Name of driver
                            </Typography>
                        </Grid>
                        <Grid item xs={5}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Comment
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="driver_name"
                                value={formik.values.driver_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                helperText={getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextFieldCustom
                                name="comment"
                                value={formik.values.comment}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default RevenueValueDriverCLVForm